import { ClipboardCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCartaoAmareloAllList,
	useDeleteCartaoAmarelo,
	useUpdateCartaoAmarelo,
} from '@/api/queries/cartao-amarelo-queries'
import {
	useDeleteDirecionada,
	useDirecionadaAllList,
	useUpdateDirecionada,
} from '@/api/queries/direcionada/direcionada-queries'
import {
	useDeleteInteracao,
	useInteracaoAllList,
	useUpdateInteracao,
} from '@/api/queries/interacao-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import {
	useDeleteVereAgir,
	useUpdateVereAgir,
	useVereAgirAllList,
} from '@/api/queries/ver-e-agir-queries'
import { CorrectInspectionFormContent } from '@/components/administration'
import { CorrectDirectedFormContent } from '@/components/administration/correct-directed-form'
import { CorrectInteracaoFormContent } from '@/components/administration/correct-interacao-form'
import { CorrectSeeActFormContent } from '@/components/administration/correct-see-act-form'
import {
	AdminCardPage,
	type CardDetail,
	CardListItem,
	ResponsiveFormModal,
} from '@/components/commons'
import { InspectionTypeButton } from '@/components/commons/inspection-type-button'
import { InspectionListFilters } from '@/components/inspections'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore, useProfileStore } from '@/store'
import {
	INSPECTION_TYPE_LABELS,
	type InspectionType,
	type Potential,
	type VereAgirOutput,
	type VereAgirUpdateInput,
} from '@/types'
import type {
	CartaoAmarelo,
	CartaoAmareloAllListParams,
	CartaoAmareloUpdateInput,
} from '@/types/cartao-amarelo'
import type {
	DirecionadaAllListParams,
	DirecionadaOutput,
	DirecionadaUpdateInput,
} from '@/types/direcionada'
import type {
	Interacao,
	InteracaoAction,
	InteracaoAllListParams,
	InteracaoUpdateInput,
} from '@/types/interacao'
import type { VereAgirAllListParams } from '@/types/ver-e-agir'
import {
	getApiErrorMessage,
	isPayloadTooLargeApiError,
} from '@/utils/api-error'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay, formatDateYMD } from '@/utils/formatDate'

type PersonEdit = {
	id: string
	name: string
	badge: string
}

type InspectionItem = {
	id: string
	type: InspectionType
	badge?: string
	titleName: string
	subtitle?: string
	details: CardDetail[]
}

type EditingItem = {
	id: string
	type: InspectionType
}

type InspectionConfig = {
	titleModal: string
	deleteItem: (id: string) => Promise<void>
	renderForm: (id: string) => React.ReactNode
}

const getSeeActStatusLabel = (status: string, t: (key: string) => string) => {
	if (status === 'IN_PROGRESS') {
		return t('inspections.seeAndAct.statusInProgress')
	}
	if (status === 'RESOLVED') {
		return t('inspections.seeAndAct.statusResolved')
	}
	return status || '-'
}

const getSeeActSituationLabel = (
	situation: string,
	t: (key: string) => string,
) => {
	if (situation === 'STOP_AND_FIX') {
		return t('inspections.seeAndAct.situationStopAndFix')
	}
	if (situation === 'FIX_AND_CONTINUE') {
		return t('inspections.seeAndAct.situationFixAndContinue')
	}
	return situation || '-'
}

export const CorrectInspectionPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const userRole = useAuthStore((s) => s.user?.role)
	const { defaultCustomerId, defaultBranchId } = useProfileStore()

	// Mesma regra de permissao das telas de listagem
	const canManageBranchFilter =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const [selectedType, setSelectedType] =
		useState<InspectionType>('yellow-card')
	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [selectedAction, setSelectedAction] = useState<
		InteracaoAction | undefined
	>()
	const [selectedStatus, setSelectedStatus] = useState<string | undefined>()
	const [selectedQuizId, setSelectedQuizId] = useState<string | undefined>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 600)

	const yellowCardParams = useMemo<CartaoAmareloAllListParams>(
		() => ({
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			customerId: selectedCustomerId,
			q: debouncedSearch.trim() || undefined,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedCustomerId,
			debouncedSearch,
		],
	)

	const directedParams = useMemo<DirecionadaAllListParams>(
		() => ({
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			quizId: selectedQuizId,
			customerId: selectedCustomerId,
			q: debouncedSearch.trim() || undefined,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedQuizId,
			selectedCustomerId,
			debouncedSearch,
		],
	)

	const seeActParams = useMemo<VereAgirAllListParams>(
		() => ({
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			status: selectedStatus,
			customerId: selectedCustomerId,
			q: debouncedSearch.trim() || undefined,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedStatus,
			selectedCustomerId,
			debouncedSearch,
		],
	)

	const interactionParams = useMemo<InteracaoAllListParams>(
		() => ({
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			action: selectedAction,
			customerId: selectedCustomerId,
			q: debouncedSearch.trim() || undefined,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedAction,
			selectedCustomerId,
			debouncedSearch,
		],
	)

	const { data: cartaoAmareloData, isLoading: isLoadingCartaoAmarelo } =
		useCartaoAmareloAllList(yellowCardParams)
	const { data: interacaoData, isLoading: isLoadingInteracao } =
		useInteracaoAllList(interactionParams)
	const { data: direcionadaData, isLoading: isLoadingDirecionada } =
		useDirecionadaAllList(directedParams)
	const { data: vereAgirData, isLoading: isLoadingVereAgir } =
		useVereAgirAllList(seeActParams)
	const { data: organizationData } = useOrganization()

	const updateMutationCartaoAmarelo = useUpdateCartaoAmarelo()
	const deleteMutationCartaoAmarelo = useDeleteCartaoAmarelo()
	const updateMutationDirecionada = useUpdateDirecionada()
	const deleteMutationDirecionada = useDeleteDirecionada()
	const updateMutationVereAgir = useUpdateVereAgir()
	const deleteMutationVereAgir = useDeleteVereAgir()
	const updateMutationInteracao = useUpdateInteracao()
	const deleteMutationInteracao = useDeleteInteracao()

	const [editingItem, setEditingItem] = useState<EditingItem | null>(null)
	const [deletingItem, setDeletingItem] = useState<InspectionItem | null>(null)
	const [saveIsLoading, setSaveIsLoading] = useState(false)

	// CR of the item currently being edited, so people can be fetched already
	// scoped to it (same approach as see-and-act-page's usePeople call).
	const editingCrId = useMemo(() => {
		if (!editingItem) return undefined

		if (editingItem.type === 'yellow-card') {
			return cartaoAmareloData?.items.find((item) => item.id === editingItem.id)
				?.cr.id
		}
		if (editingItem.type === 'see-act') {
			return vereAgirData?.items.find((item) => item.id === editingItem.id)?.cr
				.id
		}
		if (editingItem.type === 'interaction') {
			return interacaoData?.items.find((item) => item.id === editingItem.id)
				?.crId
		}
		return undefined
	}, [editingItem, cartaoAmareloData, vereAgirData, interacaoData])

	const { data: peopleData } = usePeople(true, editingCrId)

	const inspectionTypes: Array<{
		type: InspectionType
		label: string
	}> = [
		{
			type: 'yellow-card',
			label: INSPECTION_TYPE_LABELS['yellow-card'],
		},
		{
			type: 'directed',
			label: INSPECTION_TYPE_LABELS.directed,
		},
		{
			type: 'see-act',
			label: INSPECTION_TYPE_LABELS['see-act'],
		},
		{
			type: 'interaction',
			label: INSPECTION_TYPE_LABELS.interaction,
		},
	]

	const peopleByCrId = (crId?: string): PersonEdit[] => {
		if (!crId || !peopleData) {
			return []
		}

		return peopleData.map((person) => ({
			id: person.id,
			name: person.name,
			badge: person.badge,
		}))
	}

	const inspections = useMemo<InspectionItem[]>(() => {
		const list: InspectionItem[] = []
		const leaderLabel = t('administration.correctInspection.leaderFilter')
		const issuerLabel = t('administration.correctInspectionForm.issuerLabel')
		const createdByLabel = t('administration.correctInspection.createdByLabel')
		const responsibleLabel = t('inspections.actionsInProgress.responsibleLabel')
		const branchLabel = t('administration.correctInspection.branchLabel')
		const contractLabel = t('administration.correctInspection.contractLabel')
		const riskLabel = t('administration.correctInspectionForm.riskLabel')
		const typologyLabel = t(
			'administration.correctInspectionForm.typologyLabel',
		)
		const statusLabel = t('inspections.seeAndAct.status')
		const situationLabel = t('inspections.seeAndAct.situation')
		const interactionActionLabel = t('inspections.interaction.immediateAction')
		const interactionActionValueLabel = {
			ORIENTED: t('inspections.interaction.actionOriented').toUpperCase(),
			CONGRATULATED: t(
				'inspections.interaction.actionCongratulated',
			).toUpperCase(),
		} as const

		const buildDetails = (details: CardDetail[]) =>
			details.filter((detail) => detail.value.trim().length > 0)

		if (cartaoAmareloData?.items) {
			list.push(
				...cartaoAmareloData.items.map((item) => ({
					id: item.id,
					type: 'yellow-card' as const,
					badge: formatDateToDisplay(item.date),
					titleName: `${leaderLabel}: ${capitalize(item.leader.name)}`,
					subtitle: `${typologyLabel}: ${capitalize(item.kind.name)} | ${riskLabel}: ${capitalize(item.risk.name)}`,
					details: buildDetails([
						{
							label: issuerLabel,
							value: capitalize(item.employee.name),
						},
						{
							label: createdByLabel,
							value: capitalize(item.createdBy.name),
						},
						{
							label: branchLabel,
							value: capitalize(item.cr.branchName ?? ''),
						},
						{
							label: contractLabel,
							value: capitalize(item.cr.name),
						},
					]),
				})),
			)
		}

		if (direcionadaData?.items) {
			list.push(
				...direcionadaData.items.map((item) => ({
					id: item.id,
					type: 'directed' as const,
					badge: formatDateToDisplay(item.date),
					titleName: capitalize(item.quizTitle),
					details: buildDetails([
						{
							label: createdByLabel,
							value: capitalize(item.createdByName),
						},
						{
							label: contractLabel,
							value: capitalize(item.crName),
						},
					]),
				})),
			)
		}

		if (vereAgirData?.items) {
			list.push(
				...vereAgirData.items.map((item) => ({
					id: item.id,
					type: 'see-act' as const,
					badge: formatDateToDisplay(item.date),
					titleName: capitalize(item.description || item.cr.name),
					subtitle: `${typologyLabel}: ${capitalize(item.kind?.name ?? '')} | ${riskLabel}: ${capitalize(item.risk?.name ?? '')}`,
					details: buildDetails([
						{
							label: createdByLabel,
							value: capitalize(item.createdBy?.name ?? ''),
						},
						{
							label: responsibleLabel,
							value: capitalize(item.responsible?.name ?? ''),
						},
						{
							label: statusLabel,
							value: getSeeActStatusLabel(item.status, t),
						},
						{
							label: situationLabel,
							value: getSeeActSituationLabel(item.situation, t),
						},
						{
							label: branchLabel,
							value: capitalize(item.cr.branchName ?? ''),
						},
						{
							label: contractLabel,
							value: capitalize(item.cr.name),
						},
					]),
				})),
			)
		}

		if (interacaoData?.items) {
			list.push(
				...interacaoData.items.map((item) => ({
					id: item.id,
					type: 'interaction' as const,
					badge: formatDateToDisplay(item.interactionDate),
					titleName: `${leaderLabel}: ${capitalize(item.leaderName)}`,
					subtitle: `${capitalize(item.classification)} | ${capitalize(item.subclassification)}`,
					details: buildDetails([
						{
							label: issuerLabel,
							value: capitalize(item.employeeName),
						},
						{
							label: interactionActionLabel,
							value:
								interactionActionValueLabel[
									item.action as keyof typeof interactionActionValueLabel
								] ?? capitalize(item.action),
						},
						{
							label: contractLabel,
							value: capitalize(item.crName),
						},
					]),
				})),
			)
		}

		return list
	}, [cartaoAmareloData, direcionadaData, interacaoData, t, vereAgirData])

	const filteredInspections = useMemo(
		() => inspections.filter((inspection) => inspection.type === selectedType),
		[inspections, selectedType],
	)

	const isLoadingByType: Record<InspectionType, boolean> = {
		'yellow-card': isLoadingCartaoAmarelo,
		directed: isLoadingDirecionada,
		'see-act': isLoadingVereAgir,
		interaction: isLoadingInteracao,
	}
	const isLoadingSelectedType = isLoadingByType[selectedType]

	const branchOptions = useMemo(() => {
		switch (selectedType) {
			case 'yellow-card':
				return (cartaoAmareloData?.facets.branches ?? []).map((branch) => ({
					value: branch.id,
					label: branch.name,
					count: branch.count,
				}))
			case 'directed':
				return (direcionadaData?.facets.branches ?? []).map((branch) => ({
					value: branch.id,
					label: branch.name,
					count: branch.count,
				}))
			case 'see-act':
				return (vereAgirData?.facets.branches ?? []).map((branch) => ({
					value: branch.id,
					label: branch.name,
					count: branch.count,
				}))
			case 'interaction':
				return (interacaoData?.facets.branches ?? []).map((branch) => ({
					value: branch.id,
					label: branch.name,
					count: branch.count,
				}))
		}
	}, [
		selectedType,
		cartaoAmareloData,
		direcionadaData,
		vereAgirData,
		interacaoData,
	])

	const contractOptions = useMemo(() => {
		switch (selectedType) {
			case 'yellow-card':
				return (cartaoAmareloData?.facets.contracts ?? []).map((contract) => ({
					value: contract.id,
					label: contract.name,
					count: contract.count,
				}))
			case 'directed':
				return (direcionadaData?.facets.crs ?? []).map((contract) => ({
					value: contract.id,
					label: contract.name,
					count: contract.count,
				}))
			case 'see-act':
				return (vereAgirData?.facets.contracts ?? []).map((contract) => ({
					value: contract.id,
					label: contract.name,
					count: contract.count,
				}))
			case 'interaction':
				return (interacaoData?.facets.crs ?? []).map((contract) => ({
					value: contract.id,
					label: contract.name,
					count: contract.count,
				}))
		}
	}, [
		selectedType,
		cartaoAmareloData,
		direcionadaData,
		vereAgirData,
		interacaoData,
	])

	const customerOptions = useMemo(() => {
		switch (selectedType) {
			case 'yellow-card':
				return (cartaoAmareloData?.facets.customers ?? []).map((customer) => ({
					value: customer.id,
					label: customer.name,
					count: customer.count,
				}))
			case 'directed':
				return (direcionadaData?.facets.customers ?? []).map((customer) => ({
					value: customer.id,
					label: customer.name,
					count: customer.count,
				}))
			case 'see-act':
				return (vereAgirData?.facets.customers ?? []).map((customer) => ({
					value: customer.id,
					label: customer.name,
					count: customer.count,
				}))
			case 'interaction':
				return (interacaoData?.facets.customers ?? []).map((customer) => ({
					value: customer.id,
					label: customer.name,
					count: customer.count,
				}))
		}
	}, [
		selectedType,
		cartaoAmareloData,
		direcionadaData,
		vereAgirData,
		interacaoData,
	])

	const shouldShowCustomerFilter =
		customerOptions.length > 0 || Boolean(selectedCustomerId)

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const actionOptions = useMemo(
		() =>
			(interacaoData?.facets.actions ?? []).map((action) => ({
				value: action.value,
				label:
					action.value === 'ORIENTED'
						? t('inspections.interaction.actionOriented')
						: action.value === 'CONGRATULATED'
							? t('inspections.interaction.actionCongratulated')
							: action.value,
				count: action.count,
			})),
		[interacaoData, t],
	)

	const statusOptions = useMemo(
		() =>
			(vereAgirData?.facets.statuses ?? []).map((status) => ({
				value: status.value,
				label:
					status.value === 'IN_PROGRESS'
						? t('inspections.seeAndAct.statusInProgress')
						: status.value === 'RESOLVED'
							? t('inspections.seeAndAct.statusResolved')
							: status.value,
				count: status.count,
			})),
		[vereAgirData, t],
	)

	const quizOptions = useMemo(
		() =>
			(direcionadaData?.facets.quizzes ?? []).map((quiz) => ({
				value: quiz.id,
				label: quiz.name,
				count: quiz.count,
			})),
		[direcionadaData],
	)

	const handleSearchChange = (value: string) => {
		setSearch(value)
	}

	const handleBranchChange = (value: string) => {
		setSelectedBranchId(value || undefined)
		setSelectedCrId(undefined)
		setSelectedCustomerId(undefined)
	}

	const handleContractChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setSelectedCustomerId(undefined)
	}

	const handleActionChange = (value: string) => {
		setSelectedAction((value || undefined) as InteracaoAction | undefined)
	}

	const handleStatusChange = (value: string) => {
		setSelectedStatus(value || undefined)
	}

	const handleQuizChange = (value: string) => {
		setSelectedQuizId(value || undefined)
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
	}

	const filterSelects = [
		...(canManageBranchFilter
			? [
					{
						id: 'correct-inspection-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: handleBranchChange,
						options: branchOptions,
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		{
			id: 'correct-inspection-filter-contract',
			label: t('inspections.common.contract'),
			value: selectedCrId,
			onChange: handleContractChange,
			options: contractOptions,
			allLabel: t('inspections.directed.filterAll'),
		},
		...(selectedType === 'directed'
			? [
					{
						id: 'correct-inspection-filter-quiz',
						label: t('inspections.directed.filterByQuestionnaire'),
						value: selectedQuizId,
						onChange: handleQuizChange,
						options: quizOptions,
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		...(selectedType === 'interaction'
			? [
					{
						id: 'correct-inspection-filter-action',
						label: t('inspections.interaction.immediateAction'),
						value: selectedAction,
						onChange: handleActionChange,
						options: actionOptions,
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		...(selectedType === 'see-act'
			? [
					{
						id: 'correct-inspection-filter-status',
						label: t('inspections.seeAndAct.status'),
						value: selectedStatus,
						onChange: handleStatusChange,
						options: statusOptions,
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		...(shouldShowCustomerFilter
			? [
					{
						id: 'correct-inspection-filter-customer',
						label: t('inspections.common.customer'),
						value: selectedCustomerId,
						onChange: handleCustomerChange,
						options: customerOptions,
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
	]

	const handleSaveYellowCard = async ({
		crId,
		date,
		leaderId,
		issuerId,
		typologyId,
		riskId,
		unsafeDescription,
		safeDescription,
		potential,
		photo,
		removeImage,
	}: {
		crId?: string
		date?: Date
		leaderId?: string
		issuerId?: string
		typologyId?: string
		riskId?: string
		unsafeDescription?: string
		safeDescription?: string
		potential?: Potential
		photo?: File
		removeImage?: boolean
	}) => {
		if (!editingItem || editingItem.type !== 'yellow-card') {
			return
		}

		setSaveIsLoading(true)

		// O form ja envia somente os campos alterados pelo usuario
		const dataToUpdate: CartaoAmareloUpdateInput = {
			...(crId ? { crId } : {}),
			...(date ? { date: formatDateYMD(date) } : {}),
			...(leaderId ? { leaderId } : {}),
			...(issuerId ? { employeeId: issuerId } : {}),
			...(typologyId ? { kindId: typologyId } : {}),
			...(riskId ? { riskId } : {}),
			...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
			...(unsafeDescription !== undefined ? { unsafeDescription } : {}),
			...(safeDescription !== undefined ? { safeDescription } : {}),
			...(potential ? { potential } : {}),
			...(photo ? { image: photo } : {}),
			...(removeImage ? { removeImage: true } : {}),
		}

		try {
			await updateMutationCartaoAmarelo.mutateAsync({
				id: editingItem.id,
				data: dataToUpdate,
			})
			toast.success(t('administration.correctInspection.yellowCardEditSuccess'))
			setEditingItem(null)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		} finally {
			setSaveIsLoading(false)
		}
	}

	const handleSaveDirected = async (data: DirecionadaUpdateInput) => {
		if (!editingItem || editingItem.type !== 'directed') {
			return
		}

		setSaveIsLoading(true)
		try {
			await updateMutationDirecionada.mutateAsync({
				id: editingItem.id,
				data: {
					...data,
					...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				},
			})
			toast.success(t('administration.correctInspection.directedEditSuccess'))
			setEditingItem(null)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		} finally {
			setSaveIsLoading(false)
		}
	}

	const handleSaveSeeAct = async (data: VereAgirUpdateInput) => {
		if (!editingItem || editingItem.type !== 'see-act') {
			return
		}

		setSaveIsLoading(true)
		try {
			await updateMutationVereAgir.mutateAsync({
				id: editingItem.id,
				data: {
					...data,
					...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				},
			})
			toast.success(t('administration.correctInspection.seeActEditSuccess'))
			setEditingItem(null)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		} finally {
			setSaveIsLoading(false)
		}
	}

	const handleSaveInteraction = async (data: InteracaoUpdateInput) => {
		if (!editingItem || editingItem.type !== 'interaction') {
			return
		}

		setSaveIsLoading(true)
		try {
			await updateMutationInteracao.mutateAsync({
				id: editingItem.id,
				data: {
					...data,
					...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				},
			})
			toast.success(
				t('administration.correctInspection.interactionEditSuccess'),
			)
			setEditingItem(null)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setSaveIsLoading(false)
		}
	}

	const inspectionConfig: Record<InspectionType, InspectionConfig> = {
		'yellow-card': {
			titleModal: t('administration.correctInspection.titleModalYellowCard'),
			deleteItem: async (id) => {
				await deleteMutationCartaoAmarelo.mutateAsync(id)
			},
			renderForm: (id) => {
				const selected = cartaoAmareloData?.items.find((item) => item.id === id)
				const branch = organizationData?.find((b) =>
					b.crs.some((cr) => cr.id === selected?.cr.id),
				)
				return (
					<CorrectInspectionFormContent
						cartaoAmareloSelected={selected as CartaoAmarelo | undefined}
						persons={peopleByCrId(selected?.cr.id)}
						crList={branch?.crs}
						onSave={handleSaveYellowCard}
						isLoading={saveIsLoading}
						onClose={() => setEditingItem(null)}
					/>
				)
			},
		},
		directed: {
			titleModal: t('administration.correctInspection.titleModalDirect'),
			deleteItem: async (id) => {
				await deleteMutationDirecionada.mutateAsync(id)
			},
			renderForm: (id) => {
				const selected = direcionadaData?.items.find((item) => item.id === id)
				return (
					<CorrectDirectedFormContent
						directedSelected={selected as DirecionadaOutput | undefined}
						onSave={handleSaveDirected}
						isLoading={saveIsLoading}
						onClose={() => setEditingItem(null)}
					/>
				)
			},
		},
		'see-act': {
			titleModal: t('administration.correctInspection.titleModalSeeAct'),
			deleteItem: async (id) => {
				await deleteMutationVereAgir.mutateAsync(id)
			},
			renderForm: (id) => {
				const selected = vereAgirData?.items.find((item) => item.id === id)
				const branch = organizationData?.find((b) =>
					b.crs.some((cr) => cr.id === selected?.cr.id),
				)
				return (
					<CorrectSeeActFormContent
						seeActSelected={selected as VereAgirOutput | undefined}
						persons={peopleByCrId(selected?.cr.id)}
						crList={branch?.crs}
						onSave={handleSaveSeeAct}
						isLoading={saveIsLoading}
						onClose={() => setEditingItem(null)}
					/>
				)
			},
		},
		interaction: {
			titleModal: t('administration.correctInspection.titleModalInteraction'),
			deleteItem: async (id) => {
				await deleteMutationInteracao.mutateAsync(id)
			},
			renderForm: (id) => {
				const selected = interacaoData?.items.find((item) => item.id === id)
				const branch = organizationData?.find((b) =>
					b.crs.some((cr) => cr.id === selected?.crId),
				)
				return (
					<CorrectInteracaoFormContent
						interacaoSelected={selected as Interacao | undefined}
						persons={peopleByCrId(selected?.crId)}
						crList={branch?.crs}
						onSave={handleSaveInteraction}
						isLoading={saveIsLoading}
						onClose={() => setEditingItem(null)}
					/>
				)
			},
		},
	}

	const getTypeCount = (type: InspectionType): number => {
		return inspections.filter((inspection) => inspection.type === type).length
	}

	const handleConfirmDelete = async () => {
		if (!deletingItem) {
			return
		}

		const config = inspectionConfig[deletingItem.type]

		try {
			await config.deleteItem(deletingItem.id)
			toast.success(
				t('administration.correctInspection.deleteSuccess', {
					type: config.titleModal,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setDeletingItem(null)
		}
	}

	const activeEditConfig = editingItem
		? inspectionConfig[editingItem.type]
		: null

	return (
		<>
			<AdminCardPage
				icon={<ClipboardCheck className="size-5" />}
				title={t('administration.correctInspection.title')}
				subtitle={t('administration.correctInspection.subtitle')}
				onBack={() => navigate(ROUTES.ADMINISTRATION.INDEX)}
				footerText={`${filteredInspections.length} ${
					filteredInspections.length === 1
						? t('administration.correctInspection.countSingular')
						: t('administration.correctInspection.countPlural')
				}`}
			>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex">
						{inspectionTypes.map(({ type, label }) => (
							<InspectionTypeButton
								key={type}
								type={type}
								label={label}
								count={getTypeCount(type)}
								selected={selectedType === type}
								onClick={() => setSelectedType(type)}
							/>
						))}
					</div>

					<InspectionListFilters
						searchValue={search}
						onSearchChange={handleSearchChange}
						selects={filterSelects}
					/>

					{isLoadingSelectedType ? (
						<div className="py-8 text-center text-muted-foreground">
							{t('common.loading')}
						</div>
					) : (
						<div className="space-y-2">
							{filteredInspections.map((inspection) => {
								return (
									<CardListItem
										key={inspection.id}
										badge={inspection.badge}
										title={inspection.titleName}
										subtitle={inspection.subtitle}
										details={inspection.details}
										onEdit={() => {
											setEditingItem({
												id: inspection.id,
												type: inspection.type,
											})
										}}
										onDelete={() => setDeletingItem(inspection)}
									/>
								)
							})}
						</div>
					)}
				</div>

				<ResponsiveFormModal
					open={!!editingItem}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							setEditingItem(null)
						}
					}}
					title={activeEditConfig?.titleModal ?? ''}
					dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
				>
					{editingItem && activeEditConfig
						? activeEditConfig.renderForm(editingItem.id)
						: null}
				</ResponsiveFormModal>
			</AdminCardPage>

			<AlertDialog
				open={!!deletingItem}
				onOpenChange={() => setDeletingItem(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.correctInspection.deleteConfirmTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.correctInspection.deleteConfirmMessage', {
								title: deletingItem?.titleName,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDelete}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
